 /**
 * Subdomain Detection Utility
 * 
 * Detects the current subdomain and determines the app type
 */

/**
 * Get the current subdomain from hostname
 * @returns {string} Subdomain name
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;
  
  // Development: localhost or 127.0.0.1
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168')) {
    // Check for query parameter override in development
    console.log("import.meta.env.DEV",import.meta.env.DEV)
    if (import.meta.env.DEV) {
      const urlParams = new URLSearchParams(window.location.search);
      const devSubdomain = urlParams.get('subdomain');
      if (devSubdomain) {
        console.log('[Subdomain] Development override:', devSubdomain);
        return devSubdomain;
      }
      
      // Check environment variable
      const envSubdomain = import.meta.env.VITE_DEV_SUBDOMAIN;
      if (envSubdomain) {
        console.log('[Subdomain] Environment variable:', envSubdomain);
        return envSubdomain;
      }
    }
    
    // Default to 'www' for localhost
    return 'www';
  }
  
  // Production: Split hostname and check parts
  const parts = hostname.split('.');
  
  // eduna.com or eduna.local (no subdomain)
  if (parts.length === 2) {
    return 'www';
  }
  
  // subdomain.eduna.com or subdomain.eduna.local
  if (parts.length >= 3) {
    const subdomain = parts[0];
    
    // www.eduna.com -> 'www'
    if (subdomain === 'www') {
      return 'www';
    }
    
    // admin.eduna.com -> 'admin'
    if (subdomain === 'admin') {
      return 'admin';
    }
    
    // {tenant}.eduna.com -> tenant name
    return subdomain;
  }
  
  // Fallback
  return 'www';
};

/**
 * Get the app type based on subdomain
 * @returns {'PUBLIC' | 'PLATFORM_ADMIN' | 'TENANT'} App type
 */
export const getAppType = () => {
  const subdomain = getSubdomain();
  
  if (subdomain === 'admin') {
    return 'PLATFORM_ADMIN';
  }
  
  if (subdomain === 'www' || subdomain === 'localhost') {
    return 'PUBLIC';
  }
  
  // Any other subdomain is a tenant
  return 'TENANT';
};

/**
 * Get the base domain
 * @returns {string} Base domain
 */
export const getBaseDomain = () => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_BASE_DOMAIN || 'eduna.local';
  }
  
  return import.meta.env.VITE_BASE_DOMAIN || 'eduna.com';
};

/**
 * Build URL for a specific subdomain
 * @param {string} subdomain - Target subdomain
 * @param {string} path - Path to append
 * @returns {string} Full URL
 */
export const buildSubdomainUrl = (subdomain, path = '/') => {
  const baseDomain = getBaseDomain();
  const protocol = import.meta.env.DEV ? 'http' : 'https';
  const port = import.meta.env.DEV ? ':5173' : '';
  
  if (subdomain === 'www' || subdomain === '') {
    return `${protocol}://${baseDomain}${port}${path}`;
  }
  
  return `${protocol}://${subdomain}.${baseDomain}${port}${path}`;
};

/**
 * Get platform admin URL
 * @param {string} path - Path to append
 * @returns {string} Platform admin URL
 */
export const getPlatformAdminUrl = (path = '/') => {
  return buildSubdomainUrl('admin', path);
};

/**
 * Get public site URL
 * @param {string} path - Path to append
 * @returns {string} Public site URL
 */
export const getPublicUrl = (path = '/') => {
  return buildSubdomainUrl('www', path);
};

/**
 * Get tenant URL
 * @param {string} tenantSubdomain - Tenant subdomain
 * @param {string} path - Path to append
 * @returns {string} Tenant URL
 */
export const getTenantUrl = (tenantSubdomain, path = '/') => {
  return buildSubdomainUrl(tenantSubdomain, path);
};

/**
 * Check if current subdomain matches
 * @param {string} targetSubdomain - Subdomain to check
 * @returns {boolean} True if matches
 */
export const isSubdomain = (targetSubdomain) => {
  return getSubdomain() === targetSubdomain;
};

/**
 * Check if on platform admin subdomain
 * @returns {boolean} True if on admin subdomain
 */
export const isPlatformAdminSubdomain = () => {
  return getAppType() === 'PLATFORM_ADMIN';
};

/**
 * Check if on public subdomain
 * @returns {boolean} True if on public subdomain
 */
export const isPublicSubdomain = () => {
  return getAppType() === 'PUBLIC';
};

/**
 * Check if on tenant subdomain
 * @returns {boolean} True if on tenant subdomain
 */
export const isTenantSubdomain = () => {
  return getAppType() === 'TENANT';
};

/**
 * Redirect to subdomain
 * @param {string} subdomain - Target subdomain
 * @param {string} path - Path to navigate to
 */
export const redirectToSubdomain = (subdomain, path = '/') => {
  const url = buildSubdomainUrl(subdomain, path);
  window.location.href = url;
};

/**
 * Redirect to platform admin
 * @param {string} path - Path to navigate to
 */
export const redirectToPlatformAdmin = (path = '/') => {
  redirectToSubdomain('admin', path);
};

/**
 * Redirect to public site
 * @param {string} path - Path to navigate to
 */
export const redirectToPublic = (path = '/') => {
  redirectToSubdomain('www', path);
};

/**
 * Log subdomain information (development only)
 */
export const logSubdomainInfo = () => {
  if (import.meta.env.DEV) {
    console.group('[Subdomain Info]');
    console.log('Hostname:', window.location.hostname);
    console.log('Subdomain:', getSubdomain());
    console.log('App Type:', getAppType());
    console.log('Base Domain:', getBaseDomain());
    console.log('Is Platform Admin:', isPlatformAdminSubdomain());
    console.log('Is Public:', isPublicSubdomain());
    console.log('Is Tenant:', isTenantSubdomain());
    console.groupEnd();
  }
};

// Log on load in development
if (import.meta.env.DEV) {
  logSubdomainInfo();
}

export default {
  getSubdomain,
  getAppType,
  getBaseDomain,
  buildSubdomainUrl,
  getPlatformAdminUrl,
  getPublicUrl,
  getTenantUrl,
  isSubdomain,
  isPlatformAdminSubdomain,
  isPublicSubdomain,
  isTenantSubdomain,
  redirectToSubdomain,
  redirectToPlatformAdmin,
  redirectToPublic,
  logSubdomainInfo
};
